import React, { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Menu.css';

const PRODUCTS_PER_PAGE = 6;

const Menu = ({ title, products = [], showPrice = false, loading = false, error = null, largeCards = false }) => {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const [activeDotIndex, setActiveDotIndex] = useState(0);
  const scrollTimeoutRef = useRef(null);

  const dotCount = Math.max(1, Math.ceil(products.length / PRODUCTS_PER_PAGE));

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      const grid = gridRef.current;
      if (!grid) return;
      const { scrollLeft, offsetWidth } = grid;
      const pageWidth = offsetWidth;
      const index = Math.round(scrollLeft / pageWidth);
      setActiveDotIndex(Math.min(Math.max(0, index), dotCount - 1));
    }, 80);
  }, [dotCount]);

  const handleDotClick = (index) => {
    const grid = gridRef.current;
    if (!grid) return;
    const pageWidth = grid.offsetWidth;
    grid.scrollTo({ left: index * pageWidth, behavior: 'smooth' });
    setActiveDotIndex(index);
  };

  const handleCardClick = (productId) => {
    if (!productId) return;
    // Home menus are built from combos, so navigate to combo detail
    navigate(`/combo/${productId}`);
  };

  return (
    <section className="menu-section">
      <div className="container">
        <h2 className="section-title">{title}</h2>
        {error && (
          <p className="menu-error">No se pudieron cargar los productos. Verifica que la API esté disponible.</p>
        )}
        {loading && (
          <p className="menu-loading">Cargando menú...</p>
        )}
        {!loading && !error && products.length > 0 && (
          <>
            <div
              ref={gridRef}
              className={`menu-grid${largeCards ? ' menu-grid-large' : ''}`}
              onScroll={handleScroll}
              role="region"
              aria-label={`${title}, desplaza horizontalmente para ver más`}
            >
              {products.map((product, index) => (
                <div
                  key={product.id ?? index}
                  className={`menu-card${largeCards ? ' menu-card-large' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCardClick(product.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCardClick(product.id);
                    }
                  }}
                  aria-label={product.name}
                >
                  <div className="menu-card-image">
                    {product.image ? (
                      <img src={product.image} alt={product.name} onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="menu-card-image-placeholder" aria-hidden />
                    )}
                  </div>
                  <div className="menu-card-content">
                    <h3 className="menu-card-name">{product.name}</h3>
                    {showPrice && (
                      <div className="menu-card-price">
                        <span className="price-ref">Ref. {product.price}</span>
                        <button className="add-to-cart-btn">Agregar</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="menu-dots" role="tablist" aria-label="Páginas del menú">
              {Array.from({ length: dotCount }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === activeDotIndex}
                  aria-label={`Ir a página ${i + 1} de ${dotCount}`}
                  title={`Ver productos ${i * PRODUCTS_PER_PAGE + 1} a ${Math.min((i + 1) * PRODUCTS_PER_PAGE, products.length)}`}
                  className={`dot ${i === activeDotIndex ? 'active' : ''}`}
                  onClick={() => handleDotClick(i)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Menu;

